/**
 * Real-Time Revenue Integrity Monitor
 * 
 * Displays pre-authorized CPT codes alongside the dictation and
 * auto-checks procedures as sufficient documentation evidence is
 * detected in the live transcript using the Proof Map Engine.
 * 
 * PASSIVE ONLY — no prompts, no alerts. Preserves surgeon sovereignty.
 * Flags ambiguity only when contradictory evidence is detected.
 */

import { useMemo } from 'react';
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { evaluateCptProofMap } from '@/lib/proofmap/proofMapEngine';
import { getProofMap } from '@/lib/proofmap/orthopedicRules';
import type { CptProofMapResult, ProofDocStatus } from '@/lib/proofmap/types';

interface RevenueIntegrityMonitorProps {
  authorizedCpts: Array<{ code: string; description: string }>;
  transcript: string;
}

interface CptEvidenceResult {
  code: string;
  description: string;
  proofResult: CptProofMapResult | null;
  hasRules: boolean;
}

function getStatusIcon(status: ProofDocStatus | null) {
  if (!status) return <Circle className="w-4 h-4 text-muted-foreground/30" />;
  switch (status) {
    case 'SUFFICIENT':
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    case 'AMBIGUOUS':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'INSUFFICIENT':
      return <Circle className="w-4 h-4 text-muted-foreground/40" />;
  }
}

function getStatusBg(status: ProofDocStatus | null) {
  if (!status) return 'bg-muted/20';
  switch (status) {
    case 'SUFFICIENT':
      return 'bg-emerald-500/10 border-emerald-500/20';
    case 'AMBIGUOUS':
      return 'bg-amber-500/10 border-amber-500/20';
    case 'INSUFFICIENT':
      return 'bg-muted/20';
  }
}

function getStatusLabel(status: ProofDocStatus | null) {
  if (!status) return 'Awaiting dictation';
  switch (status) {
    case 'SUFFICIENT':
      return 'Documented';
    case 'AMBIGUOUS':
      return 'Needs review';
    case 'INSUFFICIENT':
      return 'Not yet detected';
  }
}

function CptEvidenceRow({ result }: { result: CptEvidenceResult }) {
  const [expanded, setExpanded] = useState(false);
  const status = result.proofResult?.documentation_status ?? null;
  const hasTranscript = result.proofResult !== null;

  return (
    <div className={cn('rounded-lg border transition-all duration-500', getStatusBg(status))}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
      >
        {getStatusIcon(status)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono font-bold text-foreground/90">
              {result.code}
            </span>
            {!result.hasRules && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">
                No rules
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate leading-tight">
            {result.description}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {hasTranscript && result.hasRules && result.proofResult && (
            <span className="text-[10px] font-medium text-muted-foreground">
              {result.proofResult.satisfied_count}/{result.proofResult.total_requirements}
            </span>
          )}
          {result.hasRules ? (
            expanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/50" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            )
          ) : null}
        </div>
      </button>

      {expanded && result.proofResult && (
        <div className="px-3 pb-2.5 space-y-1">
          {result.proofResult.requirement_results.map((req) => (
            <div
              key={req.requirement_id}
              className="flex items-start gap-2 text-[11px] pl-1"
            >
              {req.status === 'SATISFIED' ? (
                <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
              ) : req.status === 'AMBIGUOUS' ? (
                <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
              ) : (
                <Circle className="w-3 h-3 text-muted-foreground/30 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0">
                <span
                  className={cn(
                    'font-medium',
                    req.status === 'SATISFIED'
                      ? 'text-foreground/80'
                      : req.status === 'AMBIGUOUS'
                      ? 'text-amber-600'
                      : 'text-muted-foreground/60'
                  )}
                >
                  {req.label}
                </span>
                {req.status === 'AMBIGUOUS' && req.has_contradiction && (
                  <p className="text-amber-600/80 text-[10px] italic mt-0.5">
                    Contradictory evidence detected
                  </p>
                )}
                {req.status === 'SATISFIED' && req.all_snippets.length > 0 && (
                  <p className="text-muted-foreground/50 text-[10px] mt-0.5 truncate">
                    "…{req.all_snippets[0].matched_text.slice(0, 60)}…"
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RevenueIntegrityMonitor({
  authorizedCpts,
  transcript,
}: RevenueIntegrityMonitorProps) {
  const results: CptEvidenceResult[] = useMemo(() => {
    return authorizedCpts.map((cpt) => {
      const proofMap = getProofMap(cpt.code);
      if (!proofMap || !transcript.trim()) {
        return {
          code: cpt.code,
          description: cpt.description,
          proofResult: null,
          hasRules: !!proofMap,
        };
      }

      const proofResult = evaluateCptProofMap(proofMap, [
        { text: transcript, source: 'DICTATION' },
      ]);

      return {
        code: cpt.code,
        description: cpt.description,
        proofResult,
        hasRules: true,
      };
    });
  }, [authorizedCpts, transcript]);

  const sufficientCount = results.filter(
    (r) => r.proofResult?.documentation_status === 'SUFFICIENT'
  ).length;
  const ambiguousCount = results.filter(
    (r) => r.proofResult?.documentation_status === 'AMBIGUOUS'
  ).length;
  const totalWithRules = results.filter((r) => r.hasRules).length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 px-1">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Revenue Integrity
        </h3>
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-2 px-1">
        {sufficientCount > 0 && (
          <Badge
            variant="outline"
            className="text-[10px] border-emerald-500/30 text-emerald-600 bg-emerald-500/5 gap-1"
          >
            <CheckCircle2 className="w-3 h-3" />
            {sufficientCount} documented
          </Badge>
        )}
        {ambiguousCount > 0 && (
          <Badge
            variant="outline"
            className="text-[10px] border-amber-500/30 text-amber-600 bg-amber-500/5 gap-1"
          >
            <AlertTriangle className="w-3 h-3" />
            {ambiguousCount} review
          </Badge>
        )}
        {sufficientCount === 0 && ambiguousCount === 0 && (
          <span className="text-[10px] text-muted-foreground/50">
            {transcript.trim()
              ? `0/${totalWithRules} matched`
              : 'Begin dictation…'}
          </span>
        )}
      </div>

      {/* CPT evidence cards */}
      <div className="space-y-1.5">
        {results.map((result) => (
          <CptEvidenceRow key={result.code} result={result} />
        ))}
      </div>

      {/* Passive integrity note */}
      <p className="text-[10px] text-muted-foreground/40 px-1 pt-1 border-t border-border/30">
        Evidence detected passively from dictation. Surgeon retains full clinical authority.
      </p>
    </div>
  );
}